// crewbit-admin/src/components/money/InvoiceLinkedPhotoGallery.tsx
//
// Arc C3 P4 — read-only document-level gallery of the photos attached to the
// work-order line items behind an invoice (§Money.InvoicePhotoRenderingFromLinkedJob).
// Mounted by MoneyInvoiceDetailPanel. Read-only (invoices immutable, D-A3
// document-level grain) — no upload/delete, unlike the work-order PhotoGrid.
//
// Empty / absent → renders nothing. View split from container so Storybook can
// exercise it with fixtures.

import { useState } from 'react';
import { X } from 'lucide-react';
import { useInvoiceLinkedPhotos, type InvoiceLinkedPhoto } from '../../hooks/useInvoiceLinkedPhotos';
import { palette, typography } from '../../styles/tokens';

export function InvoiceLinkedPhotoGalleryView({ photos }: { photos: InvoiceLinkedPhoto[] }) {
  const [lightbox, setLightbox] = useState(-1);
  if (photos.length === 0) return null;
  const lb = lightbox >= 0 ? photos[lightbox] : null;

  return (
    <div style={{ borderTop: `1px solid ${palette.g150}`, paddingTop: 16, marginBottom: 16 }}>
      <div style={{ fontSize: typography.size.caption, fontWeight: typography.weight.semibold, color: palette.g700, marginBottom: 12 }}>
        Photos <span style={{ fontWeight: typography.weight.regular, color: palette.g400 }}>({photos.length})</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {photos.map((p, i) => (
          <div
            key={`${p.file_url}-${i}`}
            role="button"
            tabIndex={0}
            aria-label={`View photo${p.item_description ? ` — ${p.item_description}` : ''}`}
            title={p.item_description ?? undefined}
            onClick={() => setLightbox(i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setLightbox(i);
              }
            }}
            style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', cursor: 'pointer', aspectRatio: '1' }}
          >
            <img src={p.file_url} alt={p.item_description ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ))}
      </div>

      {lb && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setLightbox(-1)}
        >
          <button
            type="button"
            onClick={() => setLightbox(-1)}
            aria-label="Close photo"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 36,
              height: 36,
              borderRadius: 18,
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
          <img
            src={lb.file_url}
            alt={lb.item_description ?? ''}
            style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export function InvoiceLinkedPhotoGallery({ invoiceId }: { invoiceId: string | null }) {
  const { photos } = useInvoiceLinkedPhotos(invoiceId);
  if (photos.length === 0) return null;
  return <InvoiceLinkedPhotoGalleryView photos={photos} />;
}
